import { validate } from "uuid";
import { BindingError } from "../../BindingError.js";
import { EventManager } from "../EventManager.js";
import { MarkerComparator } from "../MarkerComparator.js";
import { RealmKeyGenerator } from "../RealmKeyGenerator.js";
import { OperationsHelpers } from "./OperationsHelpers.js";
import { EventListenersStore } from "../../treeParameters/EventListenersStore.js";
import { StateIterator } from "../state/StateIterator.js";
import { ErrorLocator } from "../exceptions/ErrorLocator.js";
import { getEntityMarker } from "../state/getEntityMarker.js";
import { LocalizedBindingError } from "../exceptions/LocalizedBindingError.js";
import { UnpersistedEntityDummyId } from "../../accessorTree/RuntimeId.js";
class ListOperations {
  constructor(accessorErrorManager, batchUpdatesOptions, eventManager, stateInitializer, treeStore) {
    this.accessorErrorManager = accessorErrorManager;
    this.batchUpdatesOptions = batchUpdatesOptions;
    this.eventManager = eventManager;
    this.stateInitializer = stateInitializer;
    this.treeStore = treeStore;
  }
  addError(listState, error) {
    return this.accessorErrorManager.addError(listState, error);
  }
  addEventListener(state, event, listener) {
    if (state.eventListeners === void 0) {
      state.eventListeners = new EventListenersStore();
    }
    return state.eventListeners.add(event, listener);
  }
  addChildEventListener(state, event, listener) {
    if (state.childEventListeners === void 0) {
      state.childEventListeners = new EventListenersStore();
    }
    return state.childEventListeners.add(event, listener);
  }
  batchUpdates(state, performUpdates) {
    this.eventManager.syncOperation(() => {
      performUpdates(state.getAccessor, this.batchUpdatesOptions);
    });
  }
  connectEntity(outerState, entityToConnect) {
    this.eventManager.syncOperation(() => {
      const persistedEntityIds = this.treeStore.getEntityListPersistedIds(outerState);
      const stateToConnect = OperationsHelpers.resolveAndPrepareEntityToConnect(this.treeStore, entityToConnect);
      const idToConnect = stateToConnect.entity.id;
      for (const state of StateIterator.eachSiblingRealmChild(this.treeStore, outerState)) {
        if (state.entityName !== stateToConnect.entity.entityName) {
          throw new BindingError(
            `EntityListAccessor.connectEntity: Attempting to connect at an entity of type '${stateToConnect.entity.entityName}' but '${state.entityName}' is expected.

Entity located at: ${ErrorLocator.locateInternalState(outerState)}.`
          );
        }
        try {
          MarkerComparator.assertEntityMarkersSubsetOf(
            state.blueprint.marker.fields,
            getEntityMarker(stateToConnect).fields
          );
        } catch (error) {
          if (error instanceof LocalizedBindingError) {
            throw new BindingError(
              `Entity list: cannot connect entity with key '${entityToConnect.key}' because its fields are incompatible with entities found on this list. Make sure both trees are equivalent.

${error.message}

` + (error.markerPath.length > 1 ? `Incompatibility found at: ${ErrorLocator.locateMarkerPath(error.markerPath)}.

` : "") + `Entity list located at: ${ErrorLocator.locateInternalState(state)}.`
            );
          }
          throw error;
        }
        if (state.children.has(idToConnect.value)) {
          continue;
        }
        this.stateInitializer.initializeEntityRealm(idToConnect, state.entityName, {
          type: "listEntity",
          parent: state
        }, stateToConnect);
        state.plannedRemovals?.delete(idToConnect.value);
        let changesDelta = 0;
        if (persistedEntityIds.has(idToConnect.value)) {
          changesDelta--;
        } else if (idToConnect.existsOnServer) {
          changesDelta++;
        }
        if (stateToConnect.type === "entityRealm") {
          changesDelta += stateToConnect.unpersistedChangesCount;
        }
        this.eventManager.registerJustUpdated(state, changesDelta);
      }
    });
  }
  disconnectEntity(listState, childEntity, options) {
    this.eventManager.syncOperation(() => {
      const persistedEntityIds = this.treeStore.getEntityListPersistedIds(listState);
      for (const state of StateIterator.eachSiblingRealmChild(this.treeStore, listState)) {
        const disconnectedChildIdValue = childEntity.id;
        const disconnectedChildRealm = state.children.get(disconnectedChildIdValue);
        if (disconnectedChildRealm === void 0) {
          throw new BindingError(
            `Entity list doesn't include an entity with id '${disconnectedChildIdValue}' and so it cannot remove it.`
          );
        }
        this.treeStore.disposeOfRealm(disconnectedChildRealm);
        listState.children.delete(disconnectedChildIdValue);
        let changesDelta = 0;
        if (persistedEntityIds.has(disconnectedChildIdValue)) {
          changesDelta++;
          if (state.plannedRemovals === void 0) {
            state.plannedRemovals = /* @__PURE__ */ new Map();
          }
          if (!options.noPersist) {
            state.plannedRemovals.set(disconnectedChildIdValue, "disconnect");
          }
        } else if (disconnectedChildRealm.entity.id.existsOnServer) {
          changesDelta--;
        }
        if (disconnectedChildRealm.type === "entityRealm") {
          changesDelta -= disconnectedChildRealm.unpersistedChangesCount;
        }
        this.eventManager.registerJustUpdated(state, changesDelta);
      }
    });
  }
  createNewEntity(outerState, initialize) {
    return this.eventManager.syncOperation(() => {
      const id = new UnpersistedEntityDummyId();
      for (const state of StateIterator.eachSiblingRealmChild(this.treeStore, outerState)) {
        const newEntity = this.stateInitializer.initializeEntityRealm(id, state.entityName, {
          type: "listEntity",
          parent: state
        });
        this.stateInitializer.runImmediateUserInitialization(newEntity, initialize);
        this.eventManager.registerJustUpdated(state, EventManager.NO_CHANGES_DIFFERENCE);
      }
      return id;
    });
  }
  getChildEntityById(state, id) {
    const realm = state.children.get(id);
    if (realm === void 0) {
      const isRuntimeId = typeof id === "number" || validate(id) || UnpersistedEntityDummyId.matchesDummyId(id);
      if (isRuntimeId) {
        throw new BindingError(`EntityList: cannot retrieve an entity with id '${id}' as it is not on the list.`);
      }
      const looksLikeKey = typeof id === "string" && (this.treeStore.entityRealmStore.has(id) || RealmKeyGenerator.vaguelyAppearsToBeAKey(id));
      throw new BindingError(
        `EntityList: cannot retrieve an entity with id '${id}' because it's not a valid id.` + (looksLikeKey ? `
The supplied value appears to be an entity *key*, not an *id*. List accessors are indexed by ids and you can use \`EntityAccessor.id\` to obtain one. Keys, on the other hand, are globally unique and you can use \`getEntityByKey\` to get its EntityAccessor.` : "")
      );
    }
    return realm.getAccessor();
  }
}
export {
  ListOperations
};
//# sourceMappingURL=ListOperations.js.map
